"use client"

import type React from "react"
import { useCallback, useEffect, useState, useRef } from "react"
import { Button } from "components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog"
import { ScrollArea } from "components/ui/scroll-area"
import { AlertCircle, Download, Eye, Loader2, ZoomIn, ZoomOut, Maximize2, Move } from "lucide-react"
import { useSignedUrlMutation } from "@/services/file"
import { downloadFromBlob } from "@/utils/download-blob"

interface FileViewerModalProps {
  file: {
    id: string
    file_name: string
    file_type: string
    url?: string
  } | null
  isOpen: boolean
  onClose: () => void
}

export const FileViewerModal = ({ file, isOpen, onClose }: FileViewerModalProps) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const [signedUrl] = useSignedUrlMutation()

  useEffect(() => {
    setImageError(false)
    setImageLoading(false)
    setFileUrl(null)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }, [file])

  useEffect(() => {
    if (file && isOpen) {
      getFileUrl()
    }
  }, [file, isOpen])

  const getFileUrl = useCallback(async () => {
    if (!file) return

    setIsLoadingUrl(true)
    try {
      if (file.url) {
        setFileUrl(file.url)
        setIsLoadingUrl(false)
        return
      }

      const response = await signedUrl({
        id: file.id,
        ResponseContentDisposition: "inline",
      }).unwrap()

      if (response?.url) {
        setFileUrl(response.url)
      } else {
        setImageError(true)
      }
    } catch (error) {
      console.error("Erro ao obter URL do arquivo:", error)
      setImageError(true)
    } finally {
      setIsLoadingUrl(false)
    }
  }, [signedUrl, file])

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 5))
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5))
  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y })
    }
  }

  const handleMouseUp = () => setIsDragging(false)

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoom((prev) => Math.max(0.5, Math.min(5, prev + delta)))
  }

  if (!file) return null

  const handleDownload = async () => {
    try {
      if (!fileUrl) {
        await getFileUrl()
        setTimeout(() => {
          if (fileUrl) downloadFile(fileUrl, file.file_name)
        }, 500)
        return
      }
      downloadFile(fileUrl, file.file_name)
    } catch (error) {
      console.error("Erro no download:", error)
    }
  }

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      downloadFromBlob(blob, filename)
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error)
      window.open(url, "_blank")
    }
  }

  const isImage = file.file_type?.startsWith("image/")
  const isPdf = file.file_type === "application/pdf"

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] w-[95vw] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b bg-muted/30">
          <div className="flex justify-between items-center gap-4">
            <DialogTitle className="text-base sm:text-lg font-semibold truncate flex-1 min-w-0">
              {file.file_name}
            </DialogTitle>
            <div className="flex gap-2 px-6">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={isLoadingUrl}
                className="h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm bg-transparent"
                title="Baixar arquivo"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Baixar</span>
              </Button>

              {(isImage || isPdf) && fileUrl && !imageError && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.5}
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0 bg-transparent"
                    title="Diminuir zoom"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetZoom}
                    className="h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm bg-transparent"
                    title="Resetar zoom"
                  >
                    <Maximize2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                    <span className="hidden sm:inline">{Math.round(zoom * 100)}%</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomIn}
                    disabled={zoom >= 5}
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                    title="Aumentar zoom"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col bg-background overflow-hidden">
          {isLoadingUrl ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center px-4">
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-muted-foreground/60 animate-spin" />
                <p className="text-base sm:text-lg font-medium mb-2">Carregando arquivo...</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Obtendo URL do arquivo</p>
              </div>
            </div>
          ) : imageError ? (
            <div className="flex-1 flex items-center justify-center text-destructive">
              <div className="text-center px-4">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-destructive/60" />
                <p className="text-base sm:text-lg font-medium mb-2">Erro ao carregar arquivo</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Não foi possível obter o arquivo.</p>
                <Button variant="outline" onClick={getFileUrl} className="mt-4 bg-transparent mx-auto" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Tentar novamente
                </Button>
              </div>
            </div>
          ) : (isImage || isPdf) && fileUrl ? (
            <div className="flex-1 relative overflow-hidden bg-muted/10">
              <ScrollArea className="h-full w-full">
                <div
                  ref={imageContainerRef}
                  className={`min-h-full flex items-center justify-center p-4 ${zoom > 1 ? "cursor-move" : "cursor-default"}`}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onWheel={handleWheel}
                >
                  <div
                    className="relative transition-transform duration-200 ease-out"
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                      transformOrigin: "center center",
                    }}
                  >
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/75 z-10 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    )}
                    {isPdf ? (
                      <iframe
                        src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                        className="w-full h-[calc(95vh-200px)] border rounded-lg shadow-lg"
                        title={file.file_name}
                        frameBorder="0"
                        onError={() => {
                          setImageError(true)
                          setImageLoading(false)
                        }}
                        onLoad={() => setImageLoading(false)}
                      />
                    ) : (
                      <img
                        src={fileUrl || undefined}
                        alt={file.file_name}
                        className={`max-w-full h-auto object-contain rounded-lg shadow-lg transition-opacity select-none ${imageLoading ? "opacity-50" : "opacity-100"}`}
                        style={{ minWidth: "200px", maxWidth: "100%", maxHeight: "calc(95vh - 200px)" }}
                        onError={() => {
                          setImageError(true)
                          setImageLoading(false)
                        }}
                        onLoad={() => setImageLoading(false)}
                        onLoadStart={() => setImageLoading(true)}
                        draggable={false}
                      />
                    )}
                  </div>
                </div>
              </ScrollArea>
              {zoom === 1 && !imageLoading && !isPdf && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-muted-foreground border shadow-sm flex items-center gap-2">
                  <Move className="w-3 h-3" />
                  <span className="hidden sm:inline">Use a roda do mouse para dar zoom</span>
                  <span className="sm:hidden">Roda do mouse = zoom</span>
                </div>
              )}
            </div>
          ) : fileUrl ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center px-4">
                <Eye className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-muted-foreground/60" />
                <p className="text-base sm:text-lg font-medium mb-2">Preview não disponível</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Este tipo de arquivo não pode ser visualizado.</p>
                <Button variant="outline" onClick={handleDownload} className="mt-4 bg-transparent mx-auto" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar arquivo
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center px-4">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-muted-foreground/60" />
                <p className="text-base sm:text-lg font-medium mb-2">Arquivo não encontrado</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Não foi possível carregar o arquivo.</p>
                <Button variant="outline" onClick={getFileUrl} className="mt-4 bg-transparent mx-auto" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Tentar novamente
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 text-xs text-muted-foreground text-center px-4 sm:px-6 py-2 sm:py-3 border-t bg-muted/30 truncate">
          <span className="font-medium">{file.file_type}</span>
          <span className="hidden sm:inline"> • {file.file_name}</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
